import {
  AppointmentTaskProxyFragment,
  Resource_AppointmentTaskStatus,
  Resource_AppointmentTaskType,
  useUpdateAppointmentTaskStatusMutation,
} from '@/generated/graphql';
import { DocumentDownloadTask } from '@components/appointment-details/appointment-tasks/tasks/document-download-task';
import { DocumentUploadAppointmentTask } from '@components/appointment-details/appointment-tasks/tasks/document-upload-appointment-task';
import { FormAppointmentTask } from '@components/appointment-details/appointment-tasks/tasks/form-appointment-task';
import { InfoAppointmentTask } from '@components/appointment-details/appointment-tasks/tasks/info-appointment-task';
import { AppointmentTaskComponent } from '@components/appointment-details/appointment-tasks/types';
import { Divider, message, Result } from 'antd';
import Title from 'antd/lib/typography/Title';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { extractTranslation } from '../../../helpers/formatters';

interface AppointmentTaskContainerProps {
  proxyAppointmentTask: AppointmentTaskProxyFragment;
  // use this callback to indicate that an action was performed which should dismiss the current selected task
  onComplete: () => void;
}

const appointmentTaskComponentMap = new Map<
  Resource_AppointmentTaskType,
  FC<AppointmentTaskComponent>
>([
  [Resource_AppointmentTaskType.AppointmentTaskTypeInfo, InfoAppointmentTask],
  [Resource_AppointmentTaskType.AppointmentTaskTypeDocumentUpload, DocumentUploadAppointmentTask],
  [Resource_AppointmentTaskType.AppointmentTaskTypeDocumentDownload, DocumentDownloadTask],
  [Resource_AppointmentTaskType.AppointmentTaskTypeForm, FormAppointmentTask],
]);

/**
 * Wrapper component for appointment tasks. Use this component to render appointment tasks uniformly.
 * @constructor
 */
export const AppointmentTaskContainer: FC<AppointmentTaskContainerProps> = ({
  proxyAppointmentTask,
  onComplete,
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const appointmentTask = proxyAppointmentTask?.appointmentTask;

  const [updateAppointmentTaskStatusMutation] = useUpdateAppointmentTaskStatusMutation();

  const appointmentTaskComponent = useMemo(() => {
    const appointmentTask = proxyAppointmentTask?.appointmentTask;

    if (
      !appointmentTask?.type ||
      appointmentTask?.type === Resource_AppointmentTaskType.AppointmentTaskTypeNone
    ) {
      console.error(
        'Cannot render appointment task content. Appointment task is not defined or of type none.',
      );
      return null;
    }

    const AppointmentTaskComponent = appointmentTaskComponentMap.get(appointmentTask.type);

    if (!AppointmentTaskComponent) {
      console.error(`Appointment task type ${AppointmentTaskComponent} not implemented.`);
      return null;
    }

    const updateAppointmentTaskStatus = (
      status: Resource_AppointmentTaskStatus,
      completing = true,
    ) => {
      const clinicId = proxyAppointmentTask.clinicId;

      if (!clinicId) {
        console.error('Unable to update appointment task status. Clinic id not defined.');
        return;
      }

      const appointmentTaskId = proxyAppointmentTask.appointmentTask?.id;

      if (!appointmentTaskId) {
        console.error('Unable to update appointment task status. Appointment task id not defined.');
        return;
      }

      updateAppointmentTaskStatusMutation({
        variables: {
          clinicId,
          request: {
            appointmentTaskId,
            newStatus: status,
          },
        },
      }).then(() => {
        if (completing) {
          message.info(formatMessage({ id: 'appointment-tasks.task-completed' }));
          onComplete();
        }
      });
    };

    return (
      <AppointmentTaskComponent
        proxyAppointmentTask={proxyAppointmentTask}
        updateAppointmentTaskStatus={updateAppointmentTaskStatus}
      />
    );
  }, [proxyAppointmentTask, updateAppointmentTaskStatusMutation]);

  if (!appointmentTask) {
    return (
      <Result
        status="warning"
        title={formatMessage({ id: 'error.title' })}
        subTitle={formatMessage({ id: 'error.message' })}
      />
    );
  }

  return (
    <div>
      {appointmentTask.title && (
        <Title level={1}>{extractTranslation(appointmentTask.title, router.locale)}</Title>
      )}
      {appointmentTask.subtitle && (
        <Title level={2}>{extractTranslation(appointmentTask.subtitle, router.locale)}</Title>
      )}
      {appointmentTask?.type !== Resource_AppointmentTaskType.AppointmentTaskTypeInfo &&
        appointmentTask.information && (
          <p style={{ color: 'darkgray' }}>
            {extractTranslation(appointmentTask.information, router.locale)}
          </p>
        )}
      <Divider />
      {appointmentTaskComponent}
    </div>
  );
};
